import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { Wallet, Loader2, CheckCircle, ExternalLink } from 'lucide-react';

const POLYGON_CHAIN_ID = '0x89'; // 137 decimals
const USDC_ADDRESS = '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174'; // Polygon USDC
const PAYMENT_CONTRACT = process.env.REACT_APP_PAYMENT_CONTRACT || '0x0000000000000000000000000000000000000000';

const PaymentCrypto = ({ amount, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState('idle'); // idle | connecting | paying | success | error
    const [error, setError] = useState(null);
    const [txHash, setTxHash] = useState(null);

    const connectAndPay = async (tokenType = 'MATIC') => {
        if (!window.ethereum) {
            alert('Please install MetaMask!');
            return;
        }

        setLoading(true);
        setStatus('connecting');
        setError(null);

        try {
            // 1. Request Accounts
            await window.ethereum.request({ method: 'eth_requestAccounts' });

            // 2. Switch to Polygon
            try {
                await window.ethereum.request({
                    method: 'wallet_switchEthereumChain',
                    params: [{ chainId: POLYGON_CHAIN_ID }],
                });
            } catch (switchError) {
                // This error code indicates that the chain has not been added to MetaMask.
                if (switchError.code === 4902) {
                    await window.ethereum.request({
                        method: 'wallet_addEthereumChain',
                        params: [{
                            chainId: POLYGON_CHAIN_ID,
                            chainName: 'Polygon Mainnet',
                            nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
                            rpcUrls: ['https://polygon-rpc.com', 'https://1rpc.io/matic', 'https://rpc.ankr.com/polygon'],
                            blockExplorerUrls: ['https://polygonscan.com/'],
                        }],
                    });
                } else {
                    throw switchError;
                }
            }

            const provider = new ethers.BrowserProvider(window.ethereum);
            const signer = await provider.getSigner();

            setStatus('paying');

            if (tokenType === 'MATIC') {
                // Enviar MATIC diretamente (valor fixo p/ teste ou dinâmico)
                // Para o MVP, enviamos 2 MATIC como no contrato
                const tx = await signer.sendTransaction({
                    to: PAYMENT_CONTRACT,
                    value: ethers.parseEther("2.0")
                });

                setTxHash(tx.hash);
                await tx.wait();
            } else {
                // ERC20 Transfer (USDC)
                const tokenContract = new ethers.Contract(
                    USDC_ADDRESS,
                    ['function transfer(address to, uint256 amount) returns (bool)'],
                    signer
                );
                const amountWei = ethers.parseUnits(amount.toString(), 6);
                const tx = await tokenContract.transfer(PAYMENT_CONTRACT, amountWei);

                setTxHash(tx.hash);
                await tx.wait();
            }

            setStatus('success');
            setTimeout(() => onSuccess(), 3000);
        } catch (err) {
            console.error('Crypto Payment Error:', err);
            setError(err.message || 'Payment failed');
            setStatus('error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6 text-center">
            {status === 'idle' && (
                <>
                    <div className="p-4 bg-indigo-500/10 rounded-2xl border border-indigo-500/20">
                        <p className="text-sm text-indigo-300">
                            Recommended for instant global access. <br />
                            Network: <strong>Polygon</strong>
                        </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <button
                            onClick={() => connectAndPay('MATIC')}
                            className="py-3 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 transition-colors font-bold text-white flex items-center justify-center gap-2"
                        >
                            <img src="https://cryptologos.cc/logos/polygon-matic-logo.png" className="w-5 h-5" alt="MATIC" />
                            MATIC
                        </button>
                        <button
                            onClick={() => connectAndPay('USDC')}
                            className="py-3 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 transition-colors font-bold text-white flex items-center justify-center gap-2"
                        >
                            <img src="https://cryptologos.cc/logos/usd-coin-usdc-logo.png" className="w-5 h-5" alt="USDC" />
                            USDC
                        </button>
                    </div>
                </>
            )}

            {(status === 'connecting' || status === 'paying') && (
                <div className="py-8 flex flex-col items-center gap-4">
                    <Loader2 className="w-12 h-12 text-indigo-500 animate-spin" />
                    <p className="font-bold text-white">
                        {status === 'connecting' ? 'Connecting MetaMask...' : 'Confirming Transaction...'}
                    </p>
                    <p className="text-xs text-gray-500">Please check your wallet extension</p>
                </div>
            )}

            {status === 'success' && (
                <div className="py-8 flex flex-col items-center gap-4 animate-in fade-in zoom-in duration-500">
                    <CheckCircle className="w-16 h-16 text-green-500" />
                    <div>
                        <p className="text-xl font-bold text-white">Payment Received!</p>
                        <p className="text-sm text-green-400">Unlocking your report...</p>
                    </div>
                    {txHash && (
                        <a
                            href={`https://polygonscan.com/tx/${txHash}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-gray-500 hover:text-indigo-400 flex items-center gap-1 underline underline-offset-4"
                        >
                            View on Explorer <ExternalLink size={10} />
                        </a>
                    )}
                </div>
            )}

            {status === 'error' && (
                <div className="py-4">
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl mb-4">
                        <p className="text-xs text-red-400 font-mono break-all">{error}</p>
                    </div>
                    <button
                        onClick={() => setStatus('idle')}
                        className="text-sm text-gray-400 hover:text-white underline"
                    >
                        Try another way
                    </button>
                </div>
            )}
        </div>
    );
};

export default PaymentCrypto;
